/* Product main: gallery, lightbox, variants, offer tiers, countdown, delivery, sticky ATC, add to cart. */
(() => {
  const formatMoney = (cents, format, strip) => {
    const fmt = format || '${{amount}}';
    const n = Number(cents) / 100;
    const fix = (v, d, ts, ds) => {
      const [i, f] = v.toFixed(d).split('.');
      const int = i.replace(/\B(?=(\d{3})+(?!\d))/g, ts);
      return f ? `${int}${ds}${f}` : int;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      const round = strip && Number.isInteger(n);
      switch (key) {
        case 'amount': return fix(n, round ? 0 : 2, ',', '.');
        case 'amount_no_decimals': return fix(n, 0, ',', '.');
        case 'amount_with_comma_separator': return fix(n, round ? 0 : 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fix(n, 0, '.', ',');
        case 'amount_with_space_separator': return fix(n, round ? 0 : 2, ' ', ',');
        case 'amount_with_apostrophe_separator': return fix(n, round ? 0 : 2, "'", '.');
        default: return fix(n, 2, ',', '.');
      }
    }).replace(/<[^>]+>/g, '');
  };
  window.formatMoney = formatMoney;

  /* Formatter built from a Liquid-rendered sample (123456789 cents in the visitor's presentment currency), so JS output
     matches the `money` filter for every market (symbol, separators, code) instead of the shop-currency money_format. */
  const sampleFormatter = (sample, strip) => {
    // (?:\2) not \2567: a backreference followed by digits is read as group 25/256 and never matches
    const m = typeof sample === 'string' && sample.match(/^([\s\S]*?)1(\D?)234(?:\2)567(\D)89([\s\S]*)$/);
    if (!m) return null;
    const [, pre, ts, ds, post] = m;
    return (cents) => {
      const n = Math.round(Number(cents)) / 100;
      const dec = strip && Number.isInteger(n) ? 0 : 2;
      const [i, f] = n.toFixed(dec).split('.');
      const int = i.replace(/\B(?=(\d{3})+(?!\d))/g, ts);
      return `${pre}${f ? `${int}${ds}${f}` : int}${post}`;
    };
  };

  const init = (root) => {
    const data = JSON.parse(root.querySelector('[data-product-json]').textContent);
    const fromSample = sampleFormatter(data.moneySample, data.stripZeros);
    const money = fromSample || ((c) => formatMoney(c, data.money, data.stripZeros));
    const form = root.querySelector('[data-product-form]');
    const variantInput = root.querySelector('[data-variant-input]');
    const qtyInput = root.querySelector('[data-qty-input]');
    const atc = root.querySelector('[data-atc]');
    const atcLabel = root.querySelector('[data-atc-label]');
    const atcPrice = root.querySelector('[data-atc-price]');
    const stickyPrice = root.querySelector('[data-sticky-price]');
    let variant = data.variants.find((v) => v.id === Number(variantInput.value)) || data.variants[0];

    /* ── Gallery ─────────────────────────────── */
    const track = root.querySelector('[data-gallery-track]');
    const thumbs = [...root.querySelectorAll('[data-thumb]')];
    const slides = track ? [...track.children] : [];
    const counter = root.querySelector('[data-gallery-counter]');
    // visible slides (gallery filter can hide some per selected options); indexes below are into this list
    const vis = () => slides.filter((s) => !s.hidden);
    let current = 0;
    const updCounter = () => { if (counter) counter.textContent = `${current + 1} / ${vis().length}`; };
    const goTo = (i, smooth = true) => {
      const v = vis();
      if (!track || !v.length) return;
      current = Math.max(0, Math.min(v.length - 1, i));
      track.scrollTo({ left: v[current].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      setActiveThumb(current);
    };
    const setActiveThumb = (i) => {
      const s = vis()[i];
      const orig = s ? slides.indexOf(s) : -1;
      thumbs.forEach((t, j) => t.classList.toggle('is-active', orig === j));
      updCounter();
      const t = thumbs[orig];
      if (t && t.parentElement) {
        const strip = t.parentElement;
        strip.scrollTo({ left: t.offsetLeft - strip.clientWidth / 2 + t.clientWidth / 2, behavior: 'smooth' });
      }
    };
    if (track) {
      let raf;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(track.scrollLeft / track.clientWidth);
          if (i !== current) { current = i; setActiveThumb(i); }
        });
      }, { passive: true });
    }
    root.querySelector('[data-gallery-prev]')?.addEventListener('click', () => goTo(current - 1));
    root.querySelector('[data-gallery-next]')?.addEventListener('click', () => goTo(current + 1));
    thumbs.forEach((t) => t.addEventListener('click', () => goTo(vis().indexOf(slides[Number(t.dataset.thumb)]))));
    // gallery filter: data-only="optIdx|value;..." shows a slide only when every listed option matches
    const applyFilter = (opts) => {
      if (!slides.some((s) => s.dataset.only)) return;
      const keep = (el) => !el.dataset.only || el.dataset.only.split(';').filter(Boolean).every((c) => {
        const [i, ...v] = c.split('|'); return opts[Number(i)] === v.join('|');
      });
      const before = vis()[current];
      slides.forEach((s, j) => { s.hidden = !keep(s); if (thumbs[j]) thumbs[j].hidden = s.hidden; });
      const idx = vis().indexOf(before);
      goTo(idx > -1 ? idx : 0, false);
    };

    /* ── Lightbox ────────────────────────────── */
    const lb = root.querySelector('[data-lightbox]');
    if (lb) {
      const lbTrack = lb.querySelector('[data-lightbox-track]');
      root.addEventListener('click', (e) => {
        const open = e.target.closest('[data-lightbox-open]');
        if (open) {
          lb.showModal();
          const s = lbTrack.querySelector(`[data-lb-index="${open.dataset.lightboxOpen}"]`);
          if (s) lbTrack.scrollTo({ left: s.offsetLeft, behavior: 'auto' });
        }
        if (e.target.closest('[data-lightbox-close]')) lb.close();
      });
    }

    /* ── Variants ────────────────────────────── */
    const optionInputs = [...root.querySelectorAll('[data-option-index]')];
    const selectedOptions = () => {
      const out = [...variant.options];
      optionInputs.filter((i) => i.checked).forEach((i) => { out[Number(i.dataset.optionIndex)] = i.value; });
      return out;
    };
    const findVariant = (opts) => data.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    const markAvailability = () => {
      const opts = selectedOptions();
      optionInputs.forEach((inp) => {
        const idx = Number(inp.dataset.optionIndex);
        const test = [...opts]; test[idx] = inp.value;
        const v = findVariant(test);
        (inp.closest('.pm__pill') || inp.closest('.pm__swatch'))?.classList.toggle('is-unavailable', !v || !v.available);
      });
    };
    optionInputs.forEach((inp) => inp.addEventListener('change', () => {
      const lbl = root.querySelector(`[data-option-value="${inp.dataset.optionIndex}"]`);
      if (lbl) lbl.textContent = inp.dataset.label || inp.value;
      const v = findVariant(selectedOptions());
      if (v) {
        variant = v;
        variantInput.value = v.id;
        const url = new URL(location.href); url.searchParams.set('variant', v.id);
        history.replaceState(null, '', url);
        applyFilter(v.options);
        if (v.media) {
          const idx = vis().findIndex((s) => Number(s.dataset.mediaId) === v.media);
          if (idx > -1) goTo(idx);
        }
        // default every per-unit select to the chosen variant
        root.querySelectorAll('[data-unit-row]').forEach((row) => {
          row.querySelectorAll('[data-unit-option]').forEach((sel) => {
            sel.value = v.options[Number(sel.dataset.unitOption)];
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      }
      markAvailability();
      render();
    }));
    markAvailability();
    applyFilter(variant.options);

    /* ── Formula cards + Duo (2 units of the selected formula, 2nd in its own colour) ── */
    const formulas = root.querySelector('[data-formulas]');
    const duo = root.querySelector('[data-duo]');
    const duoInput = duo?.querySelector('[data-duo-input]');
    const duoOn = () => !!(duoInput && duoInput.checked);
    const duoVariant = () => {
      if (!duo) return null;
      const c = duo.querySelector('[data-duo-color]:checked');
      const opts = [...variant.options];
      if (c) opts[Number(duo.dataset.duoOption)] = c.value;
      return findVariant(opts) || variant;
    };
    const placeDuo = () => {
      if (!duo || !duo.hasAttribute('data-duo-follow')) return;
      const sel = formulas.querySelector('.pm__fcard-input:checked')?.closest('.pm__fcard');
      if (sel && sel.nextElementSibling !== duo) sel.after(duo);
    };
    const renderFormulas = () => {
      if (!formulas) return;
      placeDuo();
      const fi = Number(formulas.dataset.formulaOption);
      formulas.querySelectorAll('[data-fcard]').forEach((card) => {
        const opts = [...variant.options]; opts[fi] = card.dataset.fcard;
        const v = findVariant(opts);
        if (!v) return;
        const save = (v.compare || 0) - v.price;
        const p = card.querySelector('[data-fprice]'); if (p) p.textContent = money(v.price);
        const c = card.querySelector('[data-fcmp]'); if (c) { c.textContent = money(v.compare); c.hidden = !(save > 0); }
        const s = card.querySelector('[data-fsave]'); if (s) { s.textContent = s.dataset.tpl.replace('[amount]', money(save)); s.hidden = !(save > 0); }
        const im = card.querySelector('[data-fthumb]'); if (im && v.img) im.src = v.img;
        card.classList.toggle('is-unavailable', !v.available);
      });
      if (duo) {
        const name = formulas.querySelector('.pm__fcard-input:checked')?.closest('[data-fcard]')?.dataset.fname || '';
        const d2 = duoVariant();
        const ref = variant.compare > variant.price ? variant.compare : variant.price;
        const cmp = ref * 2;
        const total = duoTotal();
        duo.querySelector('[data-duo-sub]').textContent = duo.dataset.duoTpl.replace('[name]', name);
        duo.querySelector('[data-duo-cmp]').textContent = money(cmp);
        duo.querySelector('[data-duo-total]').textContent = money(total);
        duo.querySelector('[data-duo-save]').textContent = duo.dataset.duoSaveTpl.replace('[amount]', money(cmp - total));
        const ims = duo.querySelectorAll('[data-duo-img]');
        if (ims[0] && variant.img) ims[0].src = variant.img;
        if (ims[1] && d2 && d2.img) ims[1].src = d2.img;
        duo.classList.toggle('is-on', duoOn());
      }
    };
    // Duo total = one unit's compare-at price (their "-50 %" on two); checkout matches via market-scoped discounts.
    const duoTotal = () => (variant.compare > variant.price ? variant.compare : variant.price * 2);
    duoInput?.addEventListener('change', () => render());
    duo?.querySelectorAll('[data-duo-color]').forEach((r) => r.addEventListener('change', () => { if (duoInput && !duoInput.checked) duoInput.checked = true; render(); }));

    /* ── Offers ──────────────────────────────── */
    const sets = [...root.querySelectorAll('[data-offer]')];
    let activeSet = sets[0] || null;
    if (sets.length > 1 && window.AB) {
      const arm = window.AB.pick(`offer-${data.id}`, sets.map((s) => s.dataset.offer));
      activeSet = sets.find((s) => s.dataset.offer === arm) || sets[0];
      sets.forEach((s) => { s.hidden = s !== activeSet; });
    }
    const activeTier = () => activeSet?.querySelector('.pm__tier-input:checked') || null;

    const tierTotal = (tier, unitPrice) => {
      // exact per-currency bundle total from custom.offers tier.prices (rendered by Liquid for this currency)
      if (tier.dataset.fixedTotal !== undefined && tier.dataset.fixedTotal !== '') return Number(tier.dataset.fixedTotal);
      const units = Number(tier.dataset.units) || 1;
      const val = Number(tier.dataset.dvalue) || 0;
      const base = unitPrice * units;
      let total;
      switch (tier.dataset.dtype) {
        case 'free_units': total = unitPrice * (units - val); break;
        case 'percent': total = Math.round(base * (100 - val) / 100); break;
        case 'amount': total = base - val; break;
        case 'price': total = val; break;
        default: total = base;
      }
      return Math.max(0, total);
    };

    const render = () => {
      const available = variant.available;
      let total = variant.price;
      if (activeSet) {
        activeSet.querySelectorAll('.pm__tier-input').forEach((tier) => {
          const units = Number(tier.dataset.units) || 1;
          const t = tierTotal(tier, variant.price);
          const ref = (variant.compare > variant.price ? variant.compare : variant.price) * units + (data.cmpGifts === false ? 0 : (Number(tier.dataset.giftValue) || 0));
          const label = tier.closest('.pm__tier');
          const totalEl = label.querySelector('[data-tier-total]');
          const cmpEl = label.querySelector('[data-tier-compare]');
          const eachEl = label.querySelector('[data-tier-each]');
          if (totalEl) totalEl.textContent = money(t);
          if (cmpEl) { cmpEl.textContent = money(ref); cmpEl.hidden = !(ref > t); }
          if (eachEl) eachEl.textContent = data.i18n.each.replace('__P__', money(Math.round(t / units)));
          // gift values are rendered per currency by Liquid (gift.values[currency] → value); nothing to recompute
        });
        const tier = activeTier();
        if (tier) total = tierTotal(tier, variant.price);
      } else {
        const p = root.querySelector('[data-price]');
        const c = root.querySelector('[data-compare]');
        const sv = root.querySelector('[data-save]');
        const d = duoOn();
        const cmpV = (variant.compare > variant.price ? variant.compare : variant.price) * (d ? 2 : 1);
        total = d ? duoTotal() : variant.price;
        if (p) p.textContent = money(total) + (d && duo ? ` ${duo.dataset.duoEach}` : '');
        if (c) { c.textContent = money(cmpV); c.hidden = !(cmpV > total); }
        if (sv) { sv.textContent = sv.dataset.saveTpl.replace('[amount]', money(cmpV - total)); sv.hidden = !(cmpV > total); }
      }
      renderFormulas();
      const suffix = duoOn() && duo && !activeSet ? ` ${duo.dataset.duoEach}` : '';
      if (atcPrice) atcPrice.textContent = money(total) + suffix;
      if (stickyPrice) stickyPrice.textContent = money(total) + suffix;
      // other CTAs on the page (e.g. guarantee section) mirror the selected tier's total
      document.querySelectorAll(`[data-offer-total="${data.id}"]`).forEach((el) => { el.textContent = money(total); });
      if (atc) atc.disabled = !available;
      if (atcLabel) atcLabel.textContent = available ? (atcLabel.dataset.label || atcLabel.textContent) : data.i18n.soldOut;
    };
    if (atcLabel) atcLabel.dataset.label = atcLabel.textContent;
    activeSet?.addEventListener('change', render);
    render();

    /* ── Countdown (to local midnight) ───────── */
    const cd = root.querySelector('[data-countdown-value]');
    if (cd) {
      const tick = () => {
        const now = new Date();
        const end = new Date(now); end.setHours(24, 0, 0, 0);
        const s = Math.max(0, Math.floor((end - now) / 1000));
        const p = (n) => String(n).padStart(2, '0');
        cd.textContent = `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
      };
      tick(); setInterval(tick, 1000);
    }

    /* ── Delivery dates (business days) ──────── */
    const del = root.querySelector('[data-delivery]');
    if (del) {
      const addBiz = (d, n) => { const x = new Date(d); let k = 0; while (k < n) { x.setDate(x.getDate() + 1); const w = x.getDay(); if (w !== 0 && w !== 6) k++; } return x; };
      const fmt = new Intl.DateTimeFormat(data.locale, { day: 'numeric', month: 'short' });
      const range = (a, b) => (a.toDateString() === b.toDateString() ? fmt.format(a) : `${fmt.format(a)} – ${fmt.format(b)}`);
      const now = new Date();
      const ds = del.dataset;
      if (ds.line) {
        const lf = new Intl.DateTimeFormat(data.locale, { day: 'numeric', month: 'long' });
        del.querySelector('[data-delline]').innerHTML = ds.line.replace('[from]', lf.format(addBiz(now, +ds.delMin))).replace('[to]', lf.format(addBiz(now, +ds.delMax)));
      } else {
        del.querySelector('[data-date-ordered]').textContent = fmt.format(now);
        del.querySelector('[data-date-shipped]').textContent = range(addBiz(now, +ds.shipMin), addBiz(now, +ds.shipMax));
        del.querySelector('[data-date-delivered]').textContent = range(addBiz(now, +ds.delMin), addBiz(now, +ds.delMax));
      }
    }

    /* ── Mini reviews rotator ────────────────── */
    const mr = root.querySelector('[data-minirev]');
    if (mr) {
      const cards = [...mr.querySelectorAll('[data-minirev-card]')];
      const dots = [...mr.querySelectorAll('[data-minirev-dot]')];
      let i = 0; let timer;
      const show = (n) => {
        i = (n + cards.length) % cards.length;
        cards.forEach((c, k) => c.classList.toggle('is-active', k === i));
        dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      };
      const start = () => { clearInterval(timer); if (cards.length > 1) timer = setInterval(() => show(i + 1), 5000); };
      dots.forEach((d) => d.addEventListener('click', () => { show(Number(d.dataset.minirevDot)); start(); }));
      let x0 = null;
      mr.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      mr.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0; x0 = null;
        if (Math.abs(dx) > 40) { show(i + (dx < 0 ? 1 : -1)); start(); }
      });
      start();
    }

    /* ── Size calculator ─────────────────────── */
    root.querySelectorAll('[data-sg-calc]').forEach((box) => {
      const cfg = JSON.parse(box.dataset.sgCalc);
      const inp = box.querySelector('[data-sg-in]'); const out = box.querySelector('[data-sg-out]');
      const table = box.closest('[data-sg]').querySelector('tbody');
      let unit = 0;
      const run = () => {
        const v = parseFloat(String(inp.value).replace(',', '.'));
        table.querySelectorAll('tr').forEach((r) => r.classList.remove('is-match'));
        if (!v) { out.hidden = true; return; }
        const ranges = (cfg.units[unit] || {}).ranges || [];
        let hit = ranges.find((r) => v >= r[0] && v <= r[1]);
        if (!hit) { const next = ranges.find((r) => v < r[0]); hit = next || null; }
        out.hidden = false;
        if (!hit) { out.innerHTML = cfg.too_big || 'Please contact us for help with sizing.'; return; }
        out.innerHTML = (cfg.result || 'Your size: <strong>{size}</strong>').replace('{size}', hit[2]);
        [...table.rows].forEach((r) => { if (r.cells[0].textContent.trim() === hit[2]) r.classList.add('is-match'); });
      };
      inp.addEventListener('input', run);
      box.querySelectorAll('[data-sg-unit]').forEach((b) => b.addEventListener('click', () => {
        unit = Number(b.dataset.sgUnit);
        box.querySelectorAll('[data-sg-unit]').forEach((x) => x.classList.toggle('is-active', x === b)); run();
      }));
    });

    /* ── Sticky ATC ──────────────────────────── */
    const sticky = root.querySelector('[data-sticky-atc]');
    if (sticky && atc) {
      new IntersectionObserver(([e]) => {
        sticky.hidden = e.isIntersecting || e.boundingClientRect.top > 0;
      }).observe(atc);
      sticky.querySelector('[data-sticky-submit]').addEventListener('click', () => {
        if (sticky.hasAttribute('data-sticky-scroll')) {
          const target = root.querySelector('.pm__offers, .pm__formulas') || atc;
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        atc.scrollIntoView({ behavior: 'smooth', block: 'center' });
        form.requestSubmit(atc);
      });
    }

    /* ── Submit ──────────────────────────────── */
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!variant.available) return;
      const tier = activeTier();
      const ab = window.AB ? window.AB.stamp() : '';
      const baseProps = {};
      if (ab && ab !== '{}') baseProps._ab = ab;

      let items;
      if (!tier && duoOn()) {
        const d2 = duoVariant();
        if (!d2 || !d2.available) { alert(data.i18n.unavailable); return; }
        const props = { ...baseProps, _offer: 'duo', _bundle: `${data.id}-${Date.now().toString(36)}` };
        items = d2.id === variant.id ? [{ id: variant.id, quantity: 2, properties: props }]
          : [{ id: variant.id, quantity: 1, properties: props }, { id: d2.id, quantity: 1, properties: props }];
      } else if (!tier) {
        items = [{ id: variant.id, quantity: 1, properties: baseProps }];
      } else {
        const units = Number(tier.dataset.units) || 1;
        const bundle = `${data.id}-${Date.now().toString(36)}`;
        const props = { ...baseProps, _offer: activeSet.dataset.offer, _tier: tier.value };
        if (units > 1 || tier.dataset.gifts !== 'null') props._bundle = bundle;

        // quantity per variant (per-unit pickers when shown, otherwise the selected variant)
        const counts = new Map();
        const rows = tier.closest('.pm__tier').querySelectorAll('[data-unit-row]');
        if (data.perUnit && rows.length === units) {
          for (const row of rows) {
            const opts = [...variant.options];
            row.querySelectorAll('[data-unit-option]').forEach((s) => { opts[Number(s.dataset.unitOption)] = s.value; });
            const v = findVariant(opts);
            if (!v || !v.available) {
              alert(data.i18n.unavailable);
              return;
            }
            counts.set(v.id, (counts.get(v.id) || 0) + 1);
          }
        } else {
          counts.set(variant.id, units);
        }
        items = [...counts].map(([id, quantity]) => ({ id, quantity, properties: { ...props } }));

        let gifts = [];
        try { gifts = JSON.parse(tier.dataset.gifts) || []; } catch (err) { gifts = []; }
        gifts.forEach((g) => {
          if (!g || !g.variant) return;
          items.push({ id: Number(g.variant), quantity: Number(g.qty) || 1, properties: { _gift: bundle } });
        });
      }

      atc.classList.add('is-loading');
      try {
        await window.Cart.add(items);
        window.Cart.open();
      } catch (err) {
        console.error(err);
        alert(err.message || 'Error');
      } finally {
        atc.classList.remove('is-loading');
      }
    });
  };

  /* ── Styled dropdowns for per-unit selects (native <select> stays underneath for form logic) ── */
  const enhanceSelect = (sel) => {
    if (sel.dataset.enhanced) return;
    sel.dataset.enhanced = '1';
    const isColor = sel.hasAttribute('data-color');
    const wrap = document.createElement('span'); wrap.className = 'dd';
    sel.parentNode.insertBefore(wrap, sel); wrap.appendChild(sel);
    sel.classList.add('dd__native'); sel.tabIndex = -1; sel.setAttribute('aria-hidden', 'true');
    const btn = document.createElement('button');
    btn.type = 'button'; btn.className = 'dd__btn'; btn.setAttribute('aria-haspopup', 'listbox'); btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-label', sel.getAttribute('aria-label') || '');
    const list = document.createElement('ul'); list.className = 'dd__list'; list.setAttribute('role', 'listbox'); list.hidden = true;
    const dot = (o) => {
      const s = document.createElement('span'); s.className = 'dd__dot';
      if (o.dataset.swatch) s.style.background = o.dataset.swatch;
      return s;
    };
    const item = (o, tag) => {
      const el = document.createElement(tag);
      if (isColor) el.appendChild(dot(o));
      const t = document.createElement('span'); t.className = 'dd__txt'; t.textContent = o.textContent; el.appendChild(t);
      return el;
    };
    [...sel.options].forEach((o, i) => {
      const li = item(o, 'li'); li.className = 'dd__opt'; li.setAttribute('role', 'option'); li.dataset.i = i;
      li.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); choose(i); close(); btn.focus(); });
      list.appendChild(li);
    });
    wrap.append(btn, list);
    const sync = () => {
      btn.replaceChildren(item(sel.options[sel.selectedIndex], 'span'));
      const chev = document.createElement('span'); chev.className = 'dd__chev'; btn.appendChild(chev);
      [...list.children].forEach((li, i) => li.setAttribute('aria-selected', i === sel.selectedIndex ? 'true' : 'false'));
    };
    const choose = (i) => { if (sel.selectedIndex !== i) { sel.selectedIndex = i; sel.dispatchEvent(new Event('change', { bubbles: true })); } sync(); };
    let active = -1;
    const mark = (i) => { active = i; [...list.children].forEach((li, j) => li.classList.toggle('is-active', j === i)); list.children[i]?.scrollIntoView({ block: 'nearest' }); };
    const open = () => { document.dispatchEvent(new CustomEvent('dd:open', { detail: wrap })); list.hidden = false; wrap.classList.add('is-open'); btn.setAttribute('aria-expanded', 'true'); mark(sel.selectedIndex); };
    const close = () => { list.hidden = true; wrap.classList.remove('is-open'); btn.setAttribute('aria-expanded', 'false'); };
    btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); list.hidden ? open() : close(); });
    btn.addEventListener('keydown', (e) => {
      const n = sel.options.length;
      if (['ArrowDown', 'ArrowUp'].includes(e.key)) { e.preventDefault(); if (list.hidden) open(); else mark((active + (e.key === 'ArrowDown' ? 1 : n - 1)) % n); }
      else if (['Enter', ' '].includes(e.key)) { e.preventDefault(); if (list.hidden) open(); else { choose(active); close(); } }
      else if (e.key === 'Escape') close();
    });
    document.addEventListener('click', (e) => { if (!wrap.contains(e.target)) close(); });
    document.addEventListener('dd:open', (e) => { if (e.detail !== wrap) close(); });
    sel.addEventListener('change', sync);
    sync();
  };
  const enhanceAll = () => document.querySelectorAll('.pm__unit-select').forEach(enhanceSelect);

  document.querySelectorAll('[data-product-root]').forEach(init);
  enhanceAll();
})();
